import { css } from "glamor";
import { InfoTooltip } from "./InfoTooltip";
import { PLH_POSTHOG_ENDPOINT_IDS } from "./posthogPlhEndpoints";

export interface PostHogEndpointFreshnessRow {
  endpoint: string;
  is_cached?: boolean | null;
  last_refresh?: string | null;
  extra?: Record<string, unknown>;
}

interface PostHogEndpointCacheTileProps {
  freshness?: PostHogEndpointFreshnessRow[];
  loading: boolean;
  onRequestForce: (endpointId: string) => void;
  onRequestForceAll: () => void;
}

const formatExtra = (row: PostHogEndpointFreshnessRow): string | null => {
  const ex = row.extra;
  if (!ex || Object.keys(ex).length === 0) return null;
  try {
    return JSON.stringify(ex);
  } catch {
    return null;
  }
};

const formatLastRefresh = (value: string | null | undefined): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

export const PostHogEndpointCacheTile = ({
  freshness,
  loading,
  onRequestForce,
  onRequestForceAll,
}: PostHogEndpointCacheTileProps) => {
  const byEndpoint = new Map((freshness ?? []).map((r) => [r.endpoint, r]));

  return (
    <div
      {...css({
        background: "var(--s1)",
        borderRadius: 12,
        boxShadow: "var(--shadow)",
        border: "1px solid var(--brd)",
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        marginTop: 16,
      })}
    >
      <div
        {...css({
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        })}
      >
        <div
          {...css({
            display: "flex",
            flexDirection: "column",
            gap: 6,
            minWidth: 0,
            flex: "1 1 auto",
          })}
        >
          <h3
            {...css({
              fontSize: 15,
              fontWeight: 500,
              color: "var(--t1)",
              margin: 0,
            })}
          >
            PostHog endpoint cache (diagnostic)
          </h3>
          <p
            {...css({
              fontSize: 12,
              color: "var(--t3)",
              margin: 0,
              lineHeight: 1.45,
            })}
          >
            Force-refresh bypasses PostHog&apos;s normal endpoint cache for one request. Use when metrics look
            wrong versus the warehouse; expect slower responses and higher PostHog API usage. Not a substitute for
            fixing upstream HogQL or materialization issues.
          </p>
        </div>
        <InfoTooltip text="Default dashboard loads use normal cache behavior. Buttons here add posthogForce to the next API request only." />
      </div>

      <div className="plh-posthog-cache-toolbar">
        <button
          type="button"
          className="plh-text-btn"
          disabled={loading}
          onClick={() => onRequestForceAll()}
        >
          Force refresh all endpoints (once)
        </button>
      </div>

      <div
        {...css({
          borderTop: "1px solid var(--brd)",
          paddingTop: 12,
        })}
      >
        <div className="plh-posthog-cache-table-wrap">
          <table className="plh-posthog-cache-table">
            <thead>
              <tr>
                <th className="ph-col-endpoint">Endpoint</th>
                <th className="ph-col-cached">Cached</th>
                <th className="ph-col-refresh">Last refresh</th>
                <th className="ph-col-extra">Extra</th>
                <th className="ph-col-action" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {PLH_POSTHOG_ENDPOINT_IDS.map((id) => {
                const row = byEndpoint.get(id);
                const cachedLabel =
                  row?.is_cached === true ? "Yes" : row?.is_cached === false ? "No" : "—";
                const pillClass =
                  row?.is_cached === true
                    ? "plh-status-pill plh-status-pill--warn"
                    : row?.is_cached === false
                    ? "plh-status-pill plh-status-pill--ok"
                    : "plh-status-pill";
                const extraStr = row ? formatExtra(row) : null;
                const rawRefresh = row?.last_refresh ?? null;

                return (
                  <tr key={id}>
                    <td className="ph-col-endpoint" title={id}>
                      {id}
                    </td>
                    <td className="ph-col-cached">
                      <span className={pillClass}>{cachedLabel}</span>
                    </td>
                    <td className="ph-col-refresh mono" title={rawRefresh ?? undefined}>
                      {formatLastRefresh(rawRefresh ?? undefined)}
                    </td>
                    <td className="ph-col-extra" title={extraStr ?? undefined}>
                      {extraStr ?? "—"}
                    </td>
                    <td className="ph-col-action">
                      <button
                        type="button"
                        className="plh-text-btn"
                        disabled={loading}
                        onClick={() => onRequestForce(id)}
                      >
                        Refresh now
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
